// The Daily Briefing — the morning digest for the agency's operators, written
// from the end customer's angle (canvas "Daily Briefing", direction C with
// B's numbers row and A's headline; Gio, 2026-09-18).
//
// The split the page must make visible: statuses, numbers, campaign rows,
// charts, the Today strip, the Money block and the Needs-a-human rows are
// COMPUTED by the server and render in the house's plain panels; only the
// headline, "Look at this first", the summary and each card's "In short" are
// the model's, and they sit in the accent-washed `.cp-panel` with an AI label
// and the evidence chips of what they cite. A claim whose refs do not all
// resolve is never rendered (`resolvableClaims`). A briefing with no model
// sentence at all (`degraded`) is still a complete page.
//
// Money renders only when the server sent it — the API withholds it
// for every reader who is not an admin — and it is numbers only: the week's
// basis, the outstanding and past-due totals, and every invoice in flight as
// a table row. The model never writes about money (Gio, 2026-09-19).
//
// Refresh starts a run in the background (202) and the status says while it
// is writing (2026-11-01): the current briefing stays on screen under a
// "Writing a new briefing…" line, both refresh buttons wait, and the new one
// replaces it when the status brings it. A run the reader started that ends
// failed or skipped says so; a facts-only (degraded) run is a normal result.

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { formatAttributedCount } from '@/components/performance-metric-cell';
import {
  CPClaim, CPErrorLine, CPRegenIcon, CPSkeleton, CPSparkle, useCpTip, useSkeletonStage, type CpTipApi,
} from '@/components/copilot';
import { CPVisualStrip } from '@/components/copilot-visuals';
import { QueryErrorNote } from '@/components/error-panel';
import { PageActions } from '@/components/page-actions';
import { ScrollTable } from '@/components/scroll-table';
import { useCopilotStatus, useRegenerateBriefing } from '@/api/hooks';
import { isBriefStale, resolvableClaims, resolveRefs } from '@/lib/copilot';
import {
  BRIEFING_BASIS, campaignIsDelivering, campaignStateWord, centsOf, count, cpaCents, dayShort, deliveredLabel, directRoas, endedRun, instantDayShort, invoiceStatus, modelDisplayName, paceLabel, pct, roasX,
  statusTone, ticketsOf, todayChips, todayIsQuiet, usd, usdExact, weekOf, writtenAt,
} from '@/lib/briefing';
import type {
  BriefingAction, BriefingCampaign, BriefingInvoice, BriefingMoney, BriefingProductionCard, BriefingSystemNote, BriefingToday,
  DailyBriefing,
} from '@/api/types';
import type { EvidenceItem, ExplainClaim } from '@/api/types.gen';

const VERIFIED_TIP_TEXT =
  'Every number in this briefing appears in the underlying data — the model cannot invent or compute figures.';

const CHANNEL_LABEL: Record<BriefingCampaign['channel'], string> = { meta: 'Meta', ctv: 'CTV', dsp: 'Display' };

const REFRESH_TITLE = 'Re-reads today’s numbers and writes the briefing again; it appears here when it’s ready.';
const WRITING_TITLE = 'A new briefing is being written; it appears here when it’s ready.';

// ── shared bits ─────────────────────────────────────────────────────────────

function AiFooter({ b, tipApi, onRegenerate, regenBusy }: {
  b: DailyBriefing;
  tipApi: CpTipApi;
  onRegenerate?: () => void;
  regenBusy?: boolean;
}) {
  const model = modelDisplayName(b.model);
  const writtenTip = (
    <>
      <span className="cp-tip-k">written by AI</span>
      {model
        ? `The sentences were written by ${model} from the facts shown as chips. Statuses, lists and figures are computed by Shubert Ads Manager, not by the model.`
        : 'The model did not answer this morning; every figure on the page is computed by Shubert Ads Manager.'}
    </>
  );
  const verifiedTip = (
    <>
      <span className="cp-tip-k">numbers verified</span>
      {VERIFIED_TIP_TEXT}
    </>
  );
  const sent = deliveredLabel(b);
  return (
    <div className="cp-foot">
      {b.degraded ? (
        <span className="cp-ai">Facts only · no AI text</span>
      ) : (
        <span className="cp-ai" onMouseEnter={(e) => tipApi.show(e, writtenTip)} onMouseLeave={tipApi.hide}>
          <CPSparkle size={10}/>Written by AI
        </span>
      )}
      <span className="cp-sep">·</span>
      <span className="cp-verified" onMouseEnter={(e) => tipApi.show(e, verifiedTip)} onMouseLeave={tipApi.hide}>
        Numbers verified
      </span>
      <span className="cp-sep">·</span>
      <span>{writtenAt(b.generated_at)}</span>
      {sent && (
        <>
          <span className="cp-sep">·</span>
          <span>{sent}</span>
        </>
      )}
      {onRegenerate && (
        <button type="button" className="cp-regen" title={regenBusy ? WRITING_TITLE : REFRESH_TITLE}
          aria-label="Refresh briefing" disabled={regenBusy} onClick={onRegenerate}><CPRegenIcon/></button>
      )}
    </div>
  );
}

function StatusPill({ card }: { card: BriefingProductionCard }) {
  // Quiet when nothing is delivering, by the derived state: a finished
  // campaign stays stored `live` (2026-10-19).
  const tone = card.status === 'healthy' && !card.campaigns.some(campaignIsDelivering)
    ? 'quiet'
    : statusTone(card.status);
  return (
    <span className={`cp-status-pill ${tone}`} title={card.status_reason ? `Set by rule, not by AI — ${card.status_reason}` : 'Set by rule, not by AI'}>
      <span className="d"/>{card.status_label}
    </span>
  );
}

function Claims({ claims, evidence, tipApi }: { claims: ReadonlyArray<ExplainClaim>; evidence: ReadonlyArray<EvidenceItem>; tipApi: CpTipApi }) {
  const shown = resolvableClaims(claims, evidence);
  if (shown.length === 0) return null;
  return (
    <div className="cp-claims">
      {shown.map((c, i) => <CPClaim key={i} claim={c} evidence={evidence} tipApi={tipApi}/>)}
    </div>
  );
}

// ── hero: A's headline, "Look at this first", the portfolio sentences ──────

function BriefingHero({ b, stale, tipApi, onRegenerate, regenBusy }: {
  b: DailyBriefing;
  stale: boolean;
  tipApi: CpTipApi;
  onRegenerate: () => void;
  regenBusy: boolean;
}) {
  const oneThing = b.one_thing && resolveRefs(b.one_thing, b.evidence) ? b.one_thing : null;
  return (
    <section className={'cp-brief cp-in' + (stale ? ' cp-stale' : '') + (b.degraded ? ' cp-degraded' : '')} aria-label="This morning">
      <div className="cp-brief-hd">
        <span className="cp-ai"><CPSparkle size={10}/>This morning</span>
        <span className="cp-sep">·</span>
        <span>{b.today.date_label}</span>
        <span className="cp-sep">·</span>
        <span>Written {writtenAt(b.generated_at)}</span>
        {stale && <span className="cp-brief-stale-tag">Stale · written {instantDayShort(b.generated_at)}</span>}
        {!stale && b.confidence === 'low' && !b.degraded && <span className="cp-brief-stale-tag">Partial · some lines didn’t verify</span>}
      </div>
      {b.degraded && (
        <div className="cp-degraded-note" role="status">The model didn’t answer — showing today’s facts without commentary</div>
      )}
      <h2 className="cp-brief-headline">{b.headline}</h2>
      {oneThing && (
        <div className="cp-one">
          <span className="cp-one-lbl">Look at this first</span>
          <CPClaim claim={oneThing} evidence={b.evidence} tipApi={tipApi}/>
        </div>
      )}
      <div className="cp-brief-sections">
        <Claims claims={b.summary} evidence={b.evidence} tipApi={tipApi}/>
      </div>
      {stale && <div className="cp-stale-note">This briefing is from {instantDayShort(b.generated_at)}. Refresh to write today’s.</div>}
      <AiFooter b={b} tipApi={tipApi} onRegenerate={onRegenerate} regenBusy={regenBusy}/>
    </section>
  );
}

// ── a refresh in the background ─────────────────────────────────────────────

function WritingLine({ replacing }: { replacing: boolean }) {
  return (
    <div className="cp-writing" role="status">
      <span className="cp-stage-row cp-now"><span className="cp-stage-mark"/>Writing a new briefing…</span>
      <span className="cp-writing-sub">
        {replacing ? 'The current one stays here until the new one is ready.' : 'It appears here when it’s ready.'}
      </span>
    </div>
  );
}

function RunFailedLine({ replacing, onRetry }: { replacing: boolean; onRetry: () => void }) {
  return (
    <div className="cp-panel cp-in" role="alert">
      <div className="cp-errline">
        <span>● Couldn’t write a new briefing{replacing ? ' — the one below is still the latest' : ''}</span>
        <button type="button" className="btn btn-sm cp-retry" onClick={onRetry}>Retry</button>
      </div>
    </div>
  );
}

// ── Today: lists the model never writes ─────────────────────────────────────

function TodayStrip({ today }: { today: BriefingToday }) {
  if (todayIsQuiet(today)) {
    return <div className="cp-today"><span className="cp-today-empty">Nothing launches or ends today.</span></div>;
  }
  return (
    <div className="cp-today" role="list" aria-label="Today">
      {todayChips(today).map((chip) => (
        <span key={chip.label} className="cp-tchip" role="listitem"><span className="k">{chip.label}</span>{chip.value}</span>
      ))}
    </div>
  );
}

// ── one production card ─────────────────────────────────────────────────────

function Pace({ ratio }: { ratio: number | null | undefined }) {
  const fill = ratio == null ? 0 : Math.max(0, Math.min(100, Math.round(ratio * 100)));
  return <div className="cp-pace" aria-hidden="true"><i style={{ width: `${fill}%` }}/></div>;
}

function CampaignRow({ c }: { c: BriefingCampaign }) {
  return (
    <div className="cp-campaign-row" role="listitem">
      <span className="cp-ch">{CHANNEL_LABEL[c.channel]}</span>
      <span className="cp-campaign-name">
        <Link to={c.link as never} preload="intent">{c.name}</Link>
        <span className="muted"> · {campaignStateWord(c)}</span>
        {c.attention && <span className="cp-campaign-attention"> · {c.attention}</span>}
      </span>
      <span className="cp-campaign-spend mono tnum">{c.gross_spend_cents == null ? '—' : usd(c.gross_spend_cents)} <span className="sub">of {usd(c.budget_cents)}</span></span>
      <span className="cp-campaign-pace"><Pace ratio={c.pacing.ratio}/><span className="cp-pace-lbl">{paceLabel(c.pacing)}</span></span>
      <span className="cp-campaign-flight mono">{dayShort(c.start_date)} → {dayShort(c.end_date)}</span>
    </div>
  );
}

function ProductionCard({ card, evidence, tipApi, stale }: {
  card: BriefingProductionCard;
  evidence: ReadonlyArray<EvidenceItem>;
  tipApi: CpTipApi;
  stale: boolean;
}) {
  const s = card.sales;
  const tonight = s.performances_today.length > 0
    ? s.performances_today.map((p) => (p.sold ? `${p.time} · ${p.sold} sold` : p.time)).join(' · ')
    : null;
  // Direct sales of the show's own ads, every campaign included: the figures
  // Campaigns and Reports show.
  // Unrounded, as Reports shows a modeled fraction (0.4 is a figure, not "none").
  const ticketsMicros = card.direct_tickets_micros ?? null;
  const cpa = cpaCents(card.attribution_gross_spend_cents, card.direct_tickets_micros);
  const roas = directRoas(card.direct_ticket_value_micros, card.attribution_gross_spend_cents);
  const planned = card.planned_spend_cents
    ?? card.campaigns.reduce((sum, c) => sum + (c.pacing.planned_spend_cents ?? 0), 0);
  const cardRatio = planned > 0 ? card.gross_spend_cents / planned : null;
  const impressions = card.impressions
    ?? card.campaigns.reduce((sum, c) => sum + (c.impressions ?? 0), 0);
  const dates = card.opens && card.closes ? `${dayShort(card.opens)} → ${dayShort(card.closes)}` : null;
  const meta = [card.venue, card.tag ? card.tag.toUpperCase() : null, dates].filter(Boolean).join(' · ');
  return (
    <article className={'cp-card cp-in' + (stale ? ' cp-low' : '')} aria-label={card.name}>
      <header className="cp-card-hd">
        <div className="cp-card-title">
          <h3 className="cp-card-name">{card.name}</h3>
          {meta && <div className="cp-card-meta">{meta}</div>}
          {card.status_reason && <div className="cp-card-reason">{card.status_reason}</div>}
        </div>
        <div className="cp-card-status">
          <StatusPill card={card}/>
          <span className="cp-card-rule">set by rule, not by AI</span>
        </div>
      </header>
      <div className="cp-card-cols">
        <div className="cp-card-col">
          <div className="cp-lbl">How it’s selling</div>
          <dl className="cp-rows">
            <dt>Tickets, past 7 days</dt>
            <dd>{s.tickets_7d == null ? 'no box-office figures' : <>{count(s.tickets_7d)}{s.tickets_prev_7d != null && <span className="sub"> ({count(s.tickets_prev_7d)} the week before)</span>}</>}</dd>
            {s.tickets_yesterday != null && <><dt>Yesterday</dt><dd>{count(s.tickets_yesterday)}</dd></>}
            {tonight && <><dt>Tonight</dt><dd>{tonight}</dd></>}
            {s.revenue_7d_cents != null && s.revenue_7d_cents > 0 && <><dt>Box-office revenue, 7 days</dt><dd>{usd(s.revenue_7d_cents)}</dd></>}
          </dl>
        </div>
        <div className="cp-card-col">
          <div className="cp-lbl">How the ads are doing</div>
          <dl className="cp-rows">
            <dt>Spend</dt>
            <dd>{usd(card.gross_spend_cents)} <span className="sub">of {usd(card.budget_cents)}</span></dd>
            {cardRatio != null && <><dt>Pace</dt><dd>{pct(cardRatio)} of plan</dd></>}
            <dt>Tickets from ads</dt>
            <dd>{ticketsMicros == null || ticketsMicros === 0 ? 'no tickets reported yet' : formatAttributedCount(ticketsMicros / 1_000_000)}</dd>
            {ticketsMicros != null && ticketsMicros !== 0 && card.direct_ticket_value_micros != null && <><dt>Ticket Value</dt><dd>{usd(centsOf(card.direct_ticket_value_micros))}</dd></>}
            {ticketsMicros != null && ticketsMicros !== 0 && cpa != null && <><dt>CPA</dt><dd>{usdExact(Math.round(cpa))}</dd></>}
            {ticketsMicros != null && ticketsMicros !== 0 && roas != null && <><dt>ROAS</dt><dd>{roasX(roas)}</dd></>}
            {impressions > 0 && <><dt>Impressions</dt><dd>{count(impressions)}</dd></>}
            {card.margin_cents > 0 && <><dt>Agency margin</dt><dd>{usd(card.margin_cents)}</dd></>}
          </dl>
          {cardRatio != null && <Pace ratio={cardRatio}/>}
        </div>
      </div>
      {card.visuals.length > 0 && <CPVisualStrip visuals={card.visuals}/>}
      {card.narrated && !stale && resolvableClaims(card.claims, evidence).length > 0 && (
        <div className="cp-panel cp-inshort">
          <div className="cp-inshort-hd"><span className="cp-lbl accent">In short</span><span className="cp-ai"><CPSparkle size={9}/>AI</span></div>
          <Claims claims={card.claims} evidence={evidence} tipApi={tipApi}/>
        </div>
      )}
      {card.campaigns.length > 0 && (
        <div className="cp-campaigns" role="list" aria-label={`Campaigns for ${card.name}`}>
          <div className="cp-lbl">Campaigns</div>
          {card.campaigns.map((c) => <CampaignRow key={c.campaign_id} c={c}/>)}
          {card.more_campaigns > 0 && (
            <Link className="dp-deep" to={card.link as never} preload="intent">+{card.more_campaigns} more campaign{card.more_campaigns === 1 ? '' : 's'} →</Link>
          )}
        </div>
      )}
      <footer className="cp-card-foot">
        <span className="cp-card-rule">Set by rule: status, numbers, campaigns</span>
        {card.narrated && !card.claims.length ? null : <span className="cp-ai"><CPSparkle size={9}/>Written by AI: In short</span>}
        <Link className="dp-deep" to={card.link as never} preload="intent">Open campaigns →</Link>
        <Link className="dp-deep" to={`/reports?production=${card.production_id}` as never} preload="intent">Performance →</Link>
      </footer>
    </article>
  );
}

// ── the internal blocks: money (admins), needs a human, systems ─────────────

function MoneySection({ money }: { money: BriefingMoney }) {
  const verdictTone = (v: string) => (v === 'APPROVE' || v === 'RESUME' || v === 'RESEND' ? 'needs' : 'watch');
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const billed = money.invoices.reduce((sum, inv) => sum + inv.total_cents, 0);
  return (
    <section className="panel cp-money" aria-label="Money">
      <div className="cp-side-hd">
        <h3>Money</h3>
        <span className="cp-status-pill internal"><span className="d"/>Internal</span>
      </div>
      <div className="cp-money-grid">
        <div className="cp-money-basis">
          <div className="cp-lbl">This week’s billing basis</div>
          <dl className="cp-rows">
            <dt>Week</dt><dd>{money.period_label}</dd>
            <dt>Gross so far</dt><dd>{usd(money.gross_cents)} <span className="sub">through {dayShort(money.through)}</span></dd>
            <dt>Media</dt><dd>{usd(money.media_cents)}</dd>
            <dt>Management fee · {money.fee_bps / 100}%</dt><dd>{usd(money.fee_cents)}</dd>
            <dt>Agency margin</dt><dd>{usd(money.margin_cents)}{money.margin_cents === 0 && <span className="sub"> margin off</span>}</dd>
          </dl>
        </div>
        <div className="cp-money-kpis">
          <div className="cp-money-kpi">
            <span className="cp-lbl">Outstanding</span>
            <b>{usd(money.outstanding_cents)}</b>
            <span className="sub">{money.outstanding_count === 0 ? 'nothing unpaid' : `${plural(money.outstanding_count, 'invoice')} unpaid`}</span>
          </div>
          <div className={'cp-money-kpi' + (money.past_due_count > 0 ? ' bad' : '')}>
            <span className="cp-lbl">Past due</span>
            <b>{usd(money.past_due_cents)}</b>
            <span className="sub">{money.past_due_count === 0 ? 'nothing overdue' : `${plural(money.past_due_count, 'invoice')} overdue`}</span>
          </div>
        </div>
      </div>
      {money.invoices.length === 0 ? (
        <div className="cp-side-empty">No invoices in flight.</div>
      ) : (
        <ScrollTable label="Invoices in flight" className="cp-invoice-scroll">
          <table className="tbl cp-invoice-table">
            <thead>
              <tr>
                <th>Invoice</th><th>Week</th><th>Status</th><th className="num">Total</th><th>Due</th><th className="num">Unpaid</th>
              </tr>
            </thead>
            <tbody>
              {money.invoices.map((inv: BriefingInvoice) => {
                const st = invoiceStatus(inv);
                return (
                  <tr key={inv.id}>
                    <td><Link className="mono" to={inv.link as never} preload="intent">{inv.reference}</Link></td>
                    <td className="mono">{weekOf(inv.period)}</td>
                    <td>
                      <span className={`cp-inv-status ${st.tone}`}>{st.label}</span>
                      {inv.verdict && <span className={`cp-verdict ${verdictTone(inv.verdict)}`}>{inv.verdict}</span>}
                    </td>
                    <td className="num">{usd(inv.total_cents)}</td>
                    <td className="mono">{inv.due ? dayShort(inv.due) : '—'}</td>
                    <td className={'num' + (inv.days_past_due != null ? ' bad' : '')}>{inv.amount_due_cents == null ? '—' : usd(inv.amount_due_cents)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>{plural(money.invoices.length, 'invoice')} in flight · {money.outstanding_count} unpaid · {money.past_due_count} past due</td>
                <td className="num">{usd(billed)}</td>
                <td/>
                <td className={'num' + (money.past_due_count > 0 ? ' bad' : '')}>{usd(money.outstanding_cents)}</td>
              </tr>
            </tfoot>
          </table>
        </ScrollTable>
      )}
      <Link className="dp-deep" to={'/admin/invoices' as never} preload="intent">Open invoices →</Link>
    </section>
  );
}

function NeedsHuman({ rows, more = 0 }: { rows: ReadonlyArray<BriefingAction>; more?: number }) {
  return (
    <section className="panel cp-side" aria-label="Needs a human">
      <div className="cp-side-hd"><h3>Needs a human</h3><span className="cp-status-pill internal"><span className="d"/>Internal</span></div>
      {rows.length === 0 ? (
        <div className="cp-side-empty">Nothing needs a human this morning.</div>
      ) : (
        <div className="cp-human" role="list">
          {rows.map((row, i) => (
            <Link key={i} className="cp-human-row" to={row.link as never} preload="intent" role="listitem">
              <span className="cp-human-text">{row.text}{row.detail && <span className="cp-human-detail">{row.detail}</span>}</span>
              <span className={`cp-verdict ${statusTone(row.severity)}`}>{row.verdict}</span>
            </Link>
          ))}
          {more > 0 && (
            <div className="cp-human-detail" role="listitem">+{more} more need a human, less urgent than these</div>
          )}
        </div>
      )}
    </section>
  );
}

function SystemFootnote({ system }: { system: BriefingSystemNote }) {
  return (
    <div className="cp-sysnote" role="note">
      <span className={'d' + (system.ok ? ' ok' : '')}/>
      <span>{system.text}</span>
      <Link className="dp-deep" to={system.link as never} preload="intent">Open System Health →</Link>
    </div>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────

export function DailyBriefingView() {
  const statusQ = useCopilotStatus();
  const regen = useRegenerateBriefing();
  const tipApi = useCpTip();
  const status = statusQ.data;
  // The newest finished run when the reader last asked for a refresh;
  // `undefined` until they do. It is how a run's end is told apart from the
  // one already on the status.
  const [askedAfter, setAskedAfter] = useState<string | null | undefined>(undefined);
  // Only a briefing whose sales are each show's own direct sales is shown; an
  // older one may quote other shows' sales, so it waits for the refresh.
  const stored = status?.briefing ?? null;
  const briefing = stored?.basis === BRIEFING_BASIS ? stored : null;
  const refreshing = stored != null && briefing == null;
  const enabled = status?.enabled === true;
  const stale = briefing != null && isBriefStale(briefing.generated_at, Date.now());
  const loading = statusQ.isLoading;
  const stage = useSkeletonStage(loading);
  const writing = enabled && status?.generating === true;
  // The 202 takes a moment; the buttons wait for it as for the run.
  const busy = writing || regen.isPending;
  const ended = endedRun(status, askedAfter);
  // A run that started but ended before its run was recorded leaves no newer
  // run behind: the server stopped writing and still names the run the click saw.
  const lost = regen.isSuccess && regen.data.started && askedAfter !== undefined
    && status?.generating === false
    && (status.last_generation?.finished_at ?? null) === askedAfter;
  const runFailed = !busy && (lost || ended?.outcome === 'failed' || ended?.outcome === 'skipped');
  const refresh = () => {
    setAskedAfter(status?.last_generation?.finished_at ?? null);
    regen.mutate();
  };

  const headerMeta = !enabled || !briefing || loading
    ? null
    : stale
      ? `Stale · written ${instantDayShort(briefing.generated_at)}`
      : `${briefing.today.date_label} · written ${writtenAt(briefing.generated_at)}`;

  let body;
  if (loading) {
    body = <CPSkeleton kind="brief" stage={stage}/>;
  } else if (statusQ.isError && status === undefined) {
    // A failed poll keeps the status it had (and its polling) instead.
    body = <QueryErrorNote error={statusQ.error} what="the daily briefing" onRetry={() => void statusQ.refetch()}/>;
  } else if (!enabled) {
    body = (
      <div className="dp-empty" style={{ padding: '80px 20px' }}>
        <span>The Daily Briefing is off</span>
        <span style={{ color: 'var(--ink-3)', textTransform: 'none', letterSpacing: '0.04em' }}>
          It appears here once an admin turns the Copilot on in Config.
        </span>
      </div>
    );
  } else if (!briefing) {
    body = (
      <>
        {regen.isError && <CPErrorLine label="REFRESH FAILED" error={regen.error} onRetry={refresh}/>}
        {runFailed && <RunFailedLine replacing={false} onRetry={refresh}/>}
        {writing && <WritingLine replacing={false}/>}
        <div className="dp-empty" style={{ padding: '80px 20px' }}>
          <span>{refreshing ? 'Today’s briefing is being refreshed' : 'No briefing yet'}</span>
          <span style={{ color: 'var(--ink-3)', textTransform: 'none', letterSpacing: '0.04em' }}>
            {refreshing
              ? 'It is being rewritten with each show’s own sales, the figures Campaigns shows. Refresh briefing writes it now.'
              : 'This morning’s briefing hasn’t been written — Refresh briefing writes one from today’s numbers.'}
          </span>
        </div>
      </>
    );
  } else {
    body = (
      <>
        {regen.isError && <CPErrorLine label="REFRESH FAILED" error={regen.error} onRetry={refresh}/>}
        {runFailed && <RunFailedLine replacing onRetry={refresh}/>}
        {writing && <WritingLine replacing/>}
        <div className="cp-briefing-grid">
          <div className="cp-briefing-main">
            <BriefingHero b={briefing} stale={stale} tipApi={tipApi} onRegenerate={refresh} regenBusy={busy}/>
            <div className="cp-block">
              <div className="cp-lbl">Today</div>
              <TodayStrip today={briefing.today}/>
            </div>
            <div className="cp-block-hd">
              <span className="cp-lbl">
                Shows · {briefing.productions.length} with campaigns
                {briefing.more_productions > 0 && ` · ${briefing.more_productions} more productions with campaigns`}
              </span>
              <Link className="dp-deep" to={'/campaigns' as never} preload="intent">All campaigns →</Link>
            </div>
            {briefing.productions.length === 0 ? (
              <div className="cp-side-empty">Nothing on sale carries a campaign this morning.</div>
            ) : (
              briefing.productions.map((card) => (
                <ProductionCard key={card.id} card={card} evidence={briefing.evidence} tipApi={tipApi} stale={stale}/>
              ))
            )}
            {briefing.money && <MoneySection money={briefing.money}/>}
            <SystemFootnote system={briefing.system}/>
          </div>
          <aside className="cp-briefing-side">
            <NeedsHuman rows={briefing.needs_human} more={briefing.more_needs_human ?? 0}/>
            <section className="panel cp-side" aria-label="How current">
              <div className="cp-side-hd"><h3>How current</h3></div>
              <dl className="cp-rows">
                <dt>Ad delivery</dt><dd>{briefing.system.delivery_through ? `through ${dayShort(briefing.system.delivery_through)}` : 'no day yet'}</dd>
                <dt>Ticket attribution</dt><dd>{briefing.system.attribution_through ? `through ${dayShort(briefing.system.attribution_through)}` : 'no day yet'}</dd>
                {briefing.system.ingest_lag_hours != null && <><dt>Ticket feed</dt><dd className={briefing.system.ingest_lag_hours > 12 ? 'warn' : ''}>{briefing.system.ingest_lag_hours} h behind</dd></>}
                <dt>Written</dt><dd>{writtenAt(briefing.generated_at)}</dd>
              </dl>
            </section>
          </aside>
        </div>
      </>
    );
  }

  return (
    <div className="cp-page">
      <div className="page-hd">
        <div><h1 className="page-title">Daily Briefing</h1></div>
        <PageActions style={{ alignItems: 'center', gap: 10 }}>
          {headerMeta && (
            <span data-phone="hidden" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
              {headerMeta}
            </span>
          )}
          {enabled && (
            <button className="btn" data-phone="inline" onClick={refresh} disabled={loading || busy} aria-busy={busy}
              title={busy ? WRITING_TITLE : REFRESH_TITLE}>
              <CPRegenIcon size={13}/> <span className="phone-long">Refresh briefing</span><span className="phone-short">Refresh</span>
            </button>
          )}
        </PageActions>
      </div>
      <div className="cp-page-body cp-briefing-body">{body}</div>
      <div className="dp-foot">Written from this morning’s campaign, ticket and billing data · every sentence links to its evidence</div>
      {tipApi.tipEl}
    </div>
  );
}
